import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AttachUploadButton, TooltipPlacement } from '~/components/chat/AttachUploadButton'
import { BookmarksPopover } from '~/components/chat/BookmarksPopover'
import type { BookmarksPopoverAlignment } from '~/components/chat/BookmarksPopover'
import { ModeDropdown } from '~/components/chat/ModeDropdown'
import type { AgentMode, SpeedMode } from '~/components/chat/ModeDropdown'
import { Button } from '~/components/common/Button'
import { ChatInput } from '~/components/common/ChatInput'
import type { RichMenuOption } from '~/components/common/dropdowns/RichDropdownMenu'
import { ChevronRight, Code2, Loader2, Mic, Square } from 'lucide-react'
import { BetterToast } from '~/utils/toast'

export interface SlashCommandAction {
  id: string
  label: string
  description?: string
  message: string
}

export interface SlashCommand {
  id: string
  command: string
  label: string
  description?: string
  actions: SlashCommandAction[]
}

export interface SlashCommandOpenRequest {
  commandId: string
  requestId: number
}

interface ChatBoxProps {
  // Input
  value: string
  onChange: (value: string) => void
  onSend: (value: string) => void | Promise<void>
  placeholder?: string
  animatedPlaceholder?: string
  disabled?: boolean
  multiline?: boolean
  autoFocus?: boolean
  topContent?: React.ReactNode

  // Upload control (left actions)
  onFilesSelect: (files: FileList) => void
  onFolderSelect: () => void
  currentUploadCount: number

  // Optional repo selection control (left actions)
  repoSelectSlot?: React.ReactNode
  /**
   * Renders before Agent mode + repo slot (e.g. landing “+” menu). Order: [leading] → Agent → repo.
   */
  repoSelectLeadingSlot?: React.ReactNode

  // Mode controls
  agentMode?: AgentMode
  agentModeOptions?: RichMenuOption[]
  onAgentModeChange?: (mode: AgentMode) => void

  speedMode?: SpeedMode
  speedModeOptions?: RichMenuOption[]
  onSpeedModeChange?: (mode: SpeedMode) => void
  showModeControls?: boolean

  // Additional send gating (e.g. pending uploads)
  isSendDisabled?: boolean

  /**
   * If true, ChatBox stretches to full width of its container.
   * If false, it stays constrained (looks better in ChatDisplay).
   */
  expand?: boolean

  /**
   * If true, the input grows taller as it wraps (up to a max).
   * If false, keep ChatBox height stable and scroll inside the textarea.
   */
  autoResizeInput?: boolean
  rotateDropdownArrowsOnOpen?: boolean

  /**
   * Compact layout for narrow viewports (e.g. IDE plugin).
   * Splits the action bar into two rows so nothing gets truncated.
   */
  compact?: boolean
  bookmarksPopoverAlignment?: BookmarksPopoverAlignment
  onTranscribeAudio?: (audio: Blob) => Promise<string>
  onSpeechTranscriptApplied?: () => void
  speechButtonTone?: 'primary' | 'light'
  slashCommands?: SlashCommand[]
  slashCommandOpenRequest?: SlashCommandOpenRequest | null
}

const MAX_RECORDING_MS = 15000
const RECORDING_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/ogg',
  'audio/mp4'
]

function getSupportedRecordingMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined
  }
  return RECORDING_MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type))
}

export function ChatBox({
  value,
  onChange,
  onSend,
  placeholder,
  animatedPlaceholder,
  disabled = false,
  multiline = true,
  autoFocus = true,
  topContent,
  onFilesSelect,
  onFolderSelect,
  currentUploadCount,
  repoSelectSlot,
  repoSelectLeadingSlot,
  agentMode = 'agent',
  agentModeOptions = [],
  onAgentModeChange,
  speedMode = 'verify',
  speedModeOptions = [],
  onSpeedModeChange,
  showModeControls = true,
  isSendDisabled,
  expand = true,
  autoResizeInput = true,
  rotateDropdownArrowsOnOpen = false,
  compact = false,
  bookmarksPopoverAlignment = 'landing',
  onTranscribeAudio,
  onSpeechTranscriptApplied,
  speechButtonTone = 'primary',
  slashCommands = [],
  slashCommandOpenRequest
}: ChatBoxProps) {
  const hasDraft = value.trim().length > 0
  const canSend = hasDraft && !disabled && !isSendDisabled
  const [isFocused, setIsFocused] = useState(autoFocus)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const topContentRef = useRef<HTMLDivElement>(null)
  const onChangeRef = useRef(onChange)
  const [inputMaxHeightPx, setInputMaxHeightPx] = useState<number>(320)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const recordingChunksRef = useRef<BlobPart[]>([])
  const recordingTimeoutRef = useRef<number | null>(null)
  const valueRef = useRef(value)
  const isUnmountedRef = useRef(false)
  const [activeSlashCommandId, setActiveSlashCommandId] = useState<string | null>(null)
  const [dismissedSlashValue, setDismissedSlashValue] = useState<string | null>(null)
  const [highlightedSlashCommandIndex, setHighlightedSlashCommandIndex] = useState(0)
  const [highlightedSlashActionIndex, setHighlightedSlashActionIndex] = useState(0)
  const [slashMenuPlacement, setSlashMenuPlacement] = useState<'top' | 'bottom'>('top')
  const [slashMenuMaxHeightPx, setSlashMenuMaxHeightPx] = useState(360)
  const handledSlashCommandOpenRequestRef = useRef<string | null>(null)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true
      if (recordingTimeoutRef.current !== null) {
        window.clearTimeout(recordingTimeoutRef.current)
      }
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      mediaStreamRef.current?.getTracks().forEach(track => track.stop())
    }
  }, [])

  valueRef.current = value
  onChangeRef.current = onChange

  const isSlashCandidate = value.startsWith('/') && !value.includes('\n')
  const slashQuery = isSlashCandidate ? value.slice(1).trim().toLowerCase() : ''
  const activeSlashCommand = useMemo(
    () => slashCommands.find(command => command.id === activeSlashCommandId) ?? null,
    [activeSlashCommandId, slashCommands]
  )
  const matchingSlashCommands = useMemo(() => {
    if (!isSlashCandidate) {
      return []
    }

    return slashCommands.filter(command => {
      if (!slashQuery) {
        return true
      }

      const commandName = command.command.replace(/^\//, '').toLowerCase()
      const label = command.label.toLowerCase()
      const description = command.description?.toLowerCase() ?? ''
      return (
        commandName.includes(slashQuery) ||
        label.includes(slashQuery) ||
        description.includes(slashQuery)
      )
    })
  }, [isSlashCandidate, slashCommands, slashQuery])
  const isSlashPaletteOpen =
    slashCommands.length > 0 && isSlashCandidate && dismissedSlashValue !== value && !disabled
  const showSlashCommandMenu =
    isSlashPaletteOpen && !activeSlashCommand && matchingSlashCommands.length > 0
  const showSlashActionMenu = isSlashPaletteOpen && activeSlashCommand !== null
  const activeSlashCommandFreeText =
    activeSlashCommand && value.startsWith(activeSlashCommand.command)
      ? value.slice(activeSlashCommand.command.length).trim()
      : ''
  const shouldSubmitHighlightedSlashAction = activeSlashCommandFreeText.length === 0

  useEffect(() => {
    if (!isSlashCandidate) {
      setActiveSlashCommandId(null)
      setDismissedSlashValue(null)
      setHighlightedSlashCommandIndex(0)
      setHighlightedSlashActionIndex(0)
      return
    }

    if (dismissedSlashValue !== null && dismissedSlashValue !== value) {
      setDismissedSlashValue(null)
    }

    if (activeSlashCommand && !value.startsWith(activeSlashCommand.command)) {
      setActiveSlashCommandId(null)
      setHighlightedSlashActionIndex(0)
    }
  }, [activeSlashCommand, dismissedSlashValue, isSlashCandidate, value])

  useEffect(() => {
    if (!slashCommandOpenRequest) {
      handledSlashCommandOpenRequestRef.current = null
      return
    }

    const requestKey = `${slashCommandOpenRequest.commandId}:${slashCommandOpenRequest.requestId}`

    if (handledSlashCommandOpenRequestRef.current === requestKey || disabled) {
      return
    }

    const command = slashCommands.find(
      candidate => candidate.id === slashCommandOpenRequest.commandId
    )
    if (!command) {
      return
    }

    handledSlashCommandOpenRequestRef.current = requestKey
    setActiveSlashCommandId(command.id)
    setHighlightedSlashActionIndex(0)
    setDismissedSlashValue(null)
    onChange(command.command)
  }, [disabled, onChange, slashCommandOpenRequest, slashCommands])

  useEffect(() => {
    setHighlightedSlashCommandIndex(0)
  }, [slashQuery, matchingSlashCommands.length])

  useEffect(() => {
    setHighlightedSlashActionIndex(0)
  }, [activeSlashCommandId])

  useLayoutEffect(() => {
    if (!showSlashCommandMenu && !showSlashActionMenu) {
      return
    }

    const container = containerRef.current
    if (!container || typeof window === 'undefined') {
      return
    }

    const rect = container.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
    const gap = 12
    const viewportPadding = 16
    const availableAbove = Math.max(0, rect.top - gap - viewportPadding)
    const availableBelow = Math.max(0, viewportHeight - rect.bottom - gap - viewportPadding)
    const nextPlacement =
      availableAbove >= 160 || availableAbove >= availableBelow ? 'top' : 'bottom'
    const availableSpace = nextPlacement === 'top' ? availableAbove : availableBelow

    setSlashMenuPlacement(nextPlacement)
    setSlashMenuMaxHeightPx(Math.min(360, availableSpace))
  }, [showSlashActionMenu, showSlashCommandMenu, activeSlashCommand?.actions.length])

  useEffect(() => {
    const containerMax = compact ? 300 : 320
    const paddingY = compact ? 24 : 32
    const gapBetweenTopContentAndInput = topContent ? 12 : 0
    const gapBetweenInputAndActions = multiline ? 16 : 10
    const topContentHeight = topContentRef.current?.offsetHeight ?? 0
    const actionsHeight = actionsRef.current?.offsetHeight ?? 0

    const available =
      containerMax -
      paddingY -
      topContentHeight -
      gapBetweenTopContentAndInput -
      actionsHeight -
      gapBetweenInputAndActions

    const min = multiline ? 72 : 40
    setInputMaxHeightPx(Math.max(min, available))
  }, [
    multiline,
    topContent,
    repoSelectSlot,
    repoSelectLeadingSlot,
    agentMode,
    speedMode,
    showModeControls,
    compact
  ])

  const getBorderClass = () => {
    if (disabled) {
      return 'before:border-tokens-neutral-border-2'
    }
    return isFocused
      ? 'before:border-tokens-status-link-2'
      : 'before:border-tokens-neutral-border-2'
  }

  const clearRecordingTimeout = () => {
    if (recordingTimeoutRef.current !== null) {
      window.clearTimeout(recordingTimeoutRef.current)
      recordingTimeoutRef.current = null
    }
  }

  const stopMediaStream = () => {
    mediaStreamRef.current?.getTracks().forEach(track => track.stop())
    mediaStreamRef.current = null
  }

  const stopRecording = () => {
    clearRecordingTimeout()
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop()
      return
    }
    setIsRecording(false)
    stopMediaStream()
  }

  const startRecording = async () => {
    if (!onTranscribeAudio || disabled || isTranscribing) return

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      BetterToast.error('Audio recording is not supported in this browser')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = getSupportedRecordingMimeType()
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)

      mediaStreamRef.current = stream
      mediaRecorderRef.current = recorder
      recordingChunksRef.current = []

      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data)
        }
      }

      recorder.onstop = async () => {
        clearRecordingTimeout()
        stopMediaStream()

        const audioBlob = new Blob(recordingChunksRef.current, {
          type: recorder.mimeType || mimeType || 'audio/webm'
        })
        recordingChunksRef.current = []

        if (isUnmountedRef.current) {
          return
        }

        setIsRecording(false)

        if (!audioBlob.size) {
          return
        }

        setIsTranscribing(true)
        try {
          const transcript = (await onTranscribeAudio(audioBlob)).trim()
          if (transcript) {
            const currentValue = valueRef.current
            const nextValue = currentValue.trim()
              ? `${currentValue.trimEnd()} ${transcript}`
              : transcript
            onChange(nextValue)
            onSpeechTranscriptApplied?.()
          }
        } catch (error) {
          BetterToast.error(error instanceof Error ? error.message : 'Speech transcription failed')
        } finally {
          setIsTranscribing(false)
        }
      }

      recorder.start(1000)
      setIsRecording(true)
      recordingTimeoutRef.current = window.setTimeout(stopRecording, MAX_RECORDING_MS)
    } catch (error) {
      stopMediaStream()
      BetterToast.error(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Microphone permission is required to record audio'
          : 'Failed to start audio recording'
      )
    }
  }

  const handleSend = () => {
    if (!canSend) return
    onSend(value.trim())
  }

  const selectSlashCommand = useCallback(
    (command: SlashCommand) => {
      setActiveSlashCommandId(command.id)
      setHighlightedSlashActionIndex(0)
      setDismissedSlashValue(null)
      onChange(command.command)
    },
    [onChange]
  )

  const dismissSlashPalette = useCallback(() => {
    setActiveSlashCommandId(null)
    setDismissedSlashValue(valueRef.current)
    setHighlightedSlashCommandIndex(0)
    setHighlightedSlashActionIndex(0)
  }, [])

  useEffect(() => {
    if (!isSlashPaletteOpen) {
      return
    }

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      event.preventDefault()
      dismissSlashPalette()
    }

    window.addEventListener('keydown', handleWindowKeyDown)
    return () => window.removeEventListener('keydown', handleWindowKeyDown)
  }, [dismissSlashPalette, isSlashPaletteOpen])

  useEffect(() => {
    if (!isSlashPaletteOpen) {
      return
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target
      if (target instanceof Node && containerRef.current?.contains(target)) {
        return
      }

      dismissSlashPalette()
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown)
  }, [dismissSlashPalette, isSlashPaletteOpen])

  const submitSlashAction = useCallback(
    (action: SlashCommandAction) => {
      if (disabled || isSendDisabled) return
      setActiveSlashCommandId(null)
      setDismissedSlashValue(valueRef.current)
      onSend(action.message)
    },
    [disabled, isSendDisabled, onSend]
  )

  const handleSlashCommandKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isSlashPaletteOpen) {
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        dismissSlashPalette()
        return
      }

      if (showSlashActionMenu && activeSlashCommand) {
        const actions = activeSlashCommand.actions
        if (actions.length === 0) {
          return
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          setHighlightedSlashActionIndex(index => (index + 1) % actions.length)
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          setHighlightedSlashActionIndex(index => (index - 1 + actions.length) % actions.length)
          return
        }

        if (
          event.key === 'Enter' &&
          !event.shiftKey &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          shouldSubmitHighlightedSlashAction
        ) {
          event.preventDefault()
          submitSlashAction(actions[highlightedSlashActionIndex] ?? actions[0])
        }
        return
      }

      if (!showSlashCommandMenu || matchingSlashCommands.length === 0) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlightedSlashCommandIndex(index => (index + 1) % matchingSlashCommands.length)
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlightedSlashCommandIndex(
          index => (index - 1 + matchingSlashCommands.length) % matchingSlashCommands.length
        )
        return
      }

      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault()
        const command =
          matchingSlashCommands[highlightedSlashCommandIndex] ?? matchingSlashCommands[0]
        if (command) {
          selectSlashCommand(command)
        }
      }
    },
    [
      activeSlashCommand,
      dismissSlashPalette,
      highlightedSlashActionIndex,
      highlightedSlashCommandIndex,
      isSlashPaletteOpen,
      matchingSlashCommands,
      selectSlashCommand,
      shouldSubmitHighlightedSlashAction,
      showSlashActionMenu,
      showSlashCommandMenu,
      submitSlashAction
    ]
  )

  const handleInputBlur = useCallback(() => {
    setIsFocused(false)
    if (isSlashPaletteOpen) {
      dismissSlashPalette()
    }
  }, [dismissSlashPalette, isSlashPaletteOpen])

  const sendButton = (
    <Button
      isDisabled={!canSend}
      onClick={handleSend}
      className={`${compact ? 'w-7 h-7' : 'w-8 h-8'} p-0 shrink-0`}
      data-testid='chat-send-button'
    >
      <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M8.66655 14.6666V3.60941L12.1952 7.13801L13.138 6.1952L7.99989 1.05713L2.86182 6.1952L3.80463 7.13801L7.33322 3.60941V14.6666H8.66655Z'
          fill='white'
        />
      </svg>
    </Button>
  )

  const isLightSpeechButton = speechButtonTone === 'light' && !isRecording
  const speechButtonIconClassName = `w-4 h-4 ${
    isLightSpeechButton ? 'text-tokens-neutral-bg-action' : 'text-white'
  }`
  const speechButtonClassName = `${compact ? 'w-7 h-7' : 'w-8 h-8'} p-0 shrink-0 ${
    isRecording ? 'bg-tokens-status-error-text hover:bg-tokens-status-error-text/90' : ''
  } ${isLightSpeechButton ? 'border-transparent hover:border-transparent active:border-transparent' : ''}`

  const speechButton = (
    <Button
      variant={isLightSpeechButton ? 'secondary' : 'primary'}
      isDisabled={disabled || isTranscribing}
      onClick={() => {
        if (isRecording) {
          stopRecording()
        } else {
          void startRecording()
        }
      }}
      className={speechButtonClassName}
      data-testid={isRecording ? 'chat-stop-recording-button' : 'chat-mic-button'}
      aria-label={isRecording ? 'Stop recording' : 'Record voice message'}
      title={isRecording ? 'Stop recording' : 'Record voice message'}
    >
      {isTranscribing ? (
        <Loader2 className={`${speechButtonIconClassName} animate-spin`} />
      ) : isRecording ? (
        <Square className={speechButtonIconClassName} fill='currentColor' />
      ) : (
        <Mic className={speechButtonIconClassName} />
      )}
    </Button>
  )

  const primaryActionButton =
    onTranscribeAudio && (!hasDraft || isRecording || isTranscribing) ? speechButton : sendButton

  const slashCommandMenu =
    showSlashCommandMenu || showSlashActionMenu ? (
      <div
        className={`absolute left-0 right-0 z-50 overflow-y-auto overscroll-contain rounded-[18px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-2 shadow-popover ${
          slashMenuPlacement === 'top' ? 'bottom-full mb-3' : 'top-full mt-3'
        }`}
        data-testid='slash-command-menu'
        role='menu'
        aria-label='Slash commands'
        style={{ maxHeight: slashMenuMaxHeightPx }}
      >
        {showSlashCommandMenu ? (
          <div className='space-y-1'>
            {matchingSlashCommands.map((command, index) => (
              <button
                key={command.id}
                type='button'
                role='menuitem'
                data-testid={`slash-command-${command.id}`}
                onMouseDown={event => event.preventDefault()}
                onClick={() => selectSlashCommand(command)}
                className={`flex min-h-10 w-full items-center gap-3 rounded-[8px] px-3 py-2 text-left outline-none transition-colors ${
                  index === highlightedSlashCommandIndex
                    ? 'bg-tokens-neutral-bg-grey-1'
                    : 'hover:bg-tokens-neutral-bg-grey-1'
                }`}
              >
                <span className='flex h-8 w-8 shrink-0 items-center justify-center text-tokens-neutral-icon-hover'>
                  <Code2 className='h-4 w-4' strokeWidth={1.8} aria-hidden />
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-[14px] font-medium leading-5 text-tokens-neutral-text-primary'>
                    {command.label}
                  </span>
                  {command.description ? (
                    <span className='block truncate text-[12px] leading-4 text-tokens-neutral-text-tertiary'>
                      {command.description}
                    </span>
                  ) : null}
                </span>
                <span className='shrink-0 text-[12px] leading-4 text-tokens-neutral-text-tertiary'>
                  {command.command}
                </span>
                <ChevronRight
                  className='h-4 w-4 shrink-0 text-tokens-neutral-text-tertiary'
                  strokeWidth={1.8}
                  aria-hidden
                />
              </button>
            ))}
          </div>
        ) : activeSlashCommand ? (
          <div className='space-y-1'>
            <div className='flex items-center gap-2 px-3 pb-1 pt-1 text-[12px] font-medium leading-4 text-tokens-neutral-text-tertiary'>
              <Code2 className='h-4 w-4' strokeWidth={1.8} aria-hidden />
              <span>{activeSlashCommand.label}</span>
              <span>{activeSlashCommand.command}</span>
            </div>
            {activeSlashCommand.actions.map((action, index) => (
              <button
                key={action.id}
                type='button'
                role='menuitem'
                data-testid={`slash-command-action-${action.id}`}
                disabled={disabled || isSendDisabled}
                onMouseDown={event => event.preventDefault()}
                onClick={() => submitSlashAction(action)}
                className={`flex min-h-10 w-full flex-col items-start justify-center rounded-[8px] px-3 py-2 text-left outline-none transition-colors ${
                  index === highlightedSlashActionIndex
                    ? 'bg-tokens-neutral-bg-grey-1'
                    : 'hover:bg-tokens-neutral-bg-grey-1'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span className='text-[14px] font-medium leading-5 text-tokens-neutral-text-primary'>
                  {action.label}
                </span>
                {action.description ? (
                  <span className='block w-full truncate text-[12px] leading-4 text-tokens-neutral-text-tertiary'>
                    {action.description}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    ) : null

  const renderAttachUploadButton = (tooltipPlacement: TooltipPlacement) => (
    <AttachUploadButton
      onFilesSelect={onFilesSelect}
      onFolderSelect={onFolderSelect}
      disabled={disabled}
      currentUploadCount={currentUploadCount}
      tooltipContent='Upload supporting files like docs, PDFs, images (PNG/JPG), or videos (MP4)'
      tooltipPlacement={tooltipPlacement}
    />
  )

  // Append a bookmark's body to the current draft, preserving any in-progress
  // text. We append (with a separating newline if needed) rather than replace
  // so users can compose multiple snippets — matches how the Salesforce
  // "copy-paste from the doc" workflow that inspired this feature actually
  // gets used in practice.
  const handleBookmarkInsert = useCallback((body: string) => {
    const currentValue = valueRef.current
    if (!currentValue.trim()) {
      onChangeRef.current(body)
    } else {
      const sep = currentValue.endsWith('\n') ? '' : '\n\n'
      onChangeRef.current(`${currentValue}${sep}${body}`)
    }
  }, [])

  const getCurrentDraft = useCallback(() => valueRef.current, [])

  const bookmarksButton = (
    <BookmarksPopover
      onInsert={handleBookmarkInsert}
      getCurrentDraft={getCurrentDraft}
      disabled={disabled}
      alignment={bookmarksPopoverAlignment}
    />
  )

  const agentModeDropdown = showModeControls ? (
    <ModeDropdown
      label='Agent Mode'
      options={agentModeOptions}
      selectedValue={agentMode}
      onSelect={value => onAgentModeChange?.(value as AgentMode)}
      showBorder={true}
      rotateArrowOnOpen={rotateDropdownArrowsOnOpen}
    />
  ) : null

  const renderSpeedModeDropdown = (align?: 'start' | 'center' | 'end') =>
    showModeControls && agentMode !== 'ask' ? (
      <ModeDropdown
        label='Speed Mode'
        options={speedModeOptions}
        selectedValue={speedMode}
        onSelect={value => onSpeedModeChange?.(value as SpeedMode)}
        align={align}
        rotateArrowOnOpen={rotateDropdownArrowsOnOpen}
      />
    ) : null

  return (
    <div
      ref={containerRef}
      className={`relative rounded-3xl h-auto ${compact ? 'max-h-[300px]' : 'max-h-[320px]'} overflow-visible shadow-toast ${
        expand ? 'w-full' : 'w-full max-w-[800px]'
      }
      before:content-[''] before:absolute before:inset-0 before:rounded-3xl before:border before:transition-colors before:duration-150
      ${disabled ? 'before:bg-tokens-neutral-bg-grey-1' : 'before:bg-tokens-neutral-bg-white'}
      ${getBorderClass()}
      `}
    >
      {slashCommandMenu}
      <div
        className={`relative z-10 flex flex-col h-auto overflow-hidden rounded-3xl ${
          compact ? 'max-h-[300px]' : 'max-h-[320px]'
        } ${compact ? 'p-3' : 'p-4'}`}
      >
        {topContent && (
          <div ref={topContentRef} className='mb-3 flex flex-col gap-2'>
            {topContent}
          </div>
        )}

        <ChatInput
          value={value}
          onChange={onChange}
          onSubmit={handleSend}
          placeholder={placeholder}
          animatedPlaceholder={animatedPlaceholder}
          disabled={disabled}
          multiline={multiline}
          autoFocus={autoFocus}
          autoResize={autoResizeInput}
          maxHeightPx={inputMaxHeightPx}
          baseHeightPx={compact ? 56 : undefined}
          onFocus={() => setIsFocused(true)}
          onBlur={handleInputBlur}
          onKeyDown={handleSlashCommandKeyDown}
        />

        {repoSelectSlot ? (
          <div ref={actionsRef} className={`flex-shrink-0 ${compact ? 'mt-2' : 'mt-3'}`}>
            {compact ? (
              <div className='flex flex-col gap-1.5'>
                <div className='grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1.5'>
                  <div className='flex min-w-0 items-center gap-1.5'>
                    {repoSelectLeadingSlot ? (
                      <div className='shrink-0'>{repoSelectLeadingSlot}</div>
                    ) : null}
                    {agentModeDropdown ? (
                      <div className='min-w-0 shrink-0 overflow-hidden'>{agentModeDropdown}</div>
                    ) : null}
                    <div className='shrink-0'>{bookmarksButton}</div>
                    <div className='min-w-0 flex-1 overflow-hidden'>{repoSelectSlot}</div>
                  </div>
                  {primaryActionButton}
                </div>
                <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
                  {renderSpeedModeDropdown()}
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-between gap-2'>
                <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
                  {repoSelectLeadingSlot ? (
                    <div className='shrink-0'>{repoSelectLeadingSlot}</div>
                  ) : null}
                  {agentModeDropdown ? (
                    <div className='min-w-0 shrink-0 overflow-hidden'>{agentModeDropdown}</div>
                  ) : null}
                  <div className='shrink-0'>{bookmarksButton}</div>
                  <div className='min-w-0 flex-1 overflow-hidden'>{repoSelectSlot}</div>
                </div>
                <div className='flex shrink-0 items-center gap-2'>
                  {renderSpeedModeDropdown('end')}
                  {primaryActionButton}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            ref={actionsRef}
            className='flex items-center justify-between gap-2 flex-shrink-0 mt-3'
          >
            <div className='flex items-center gap-2 min-w-0'>
              <div className='shrink-0'>{renderAttachUploadButton('left')}</div>
              {agentModeDropdown}
              <div className='shrink-0'>{bookmarksButton}</div>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              {renderSpeedModeDropdown('end')}
              {primaryActionButton}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
